import { useState, useMemo, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { AppIcon } from '../ui/icon'
import { mdiCallSplit, mdiPlus, mdiClose } from '@mdi/js'
import { pdfjs } from 'react-pdf'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Badge } from '../ui/badge'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogFooter,
    DialogTitle,
    DialogDescription,
} from '../ui/dialog'
import { useApi } from '../../api/hooks'
import { useDocumentTypes } from '../../hooks/useQueries'
import { apiBaseUrl } from '../../api/client'
import type { DocumentSplitProposal } from '../../api/schema'

// Same CDN worker as the other pdfjs consumer in this codebase
// (front/src/routes/$org/debug_.annotations.tsx) — keeps a single source of
// truth for the workerSrc version string.
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`

interface SegmentRow {
    pages: string
    documentTypeCode: string
    name: string
    // Read-only context from a bundle detection proposal.
    confidence?: number
    boilerplate?: boolean
}

interface DocumentSplitDialogProps {
    org: string
    documentUid: string
    /**
     * Total page count of the parent PDF, when already known by the caller.
     * When omitted, the dialog derives it itself (headless pdfjs load of the
     * document's content URL) the first time it is opened, so the client-side
     * out-of-range check has something to validate against before the POST
     * round-trip — the server remains the authority either way.
     */
    pageCount?: number
    /**
     * A pending bundle detection proposal (spec 2026-10-14-15). When set, the
     * trigger reads « review the proposed split », the dialog opens prefilled
     * with its segments, and a « not a bundle » action rejects it.
     */
    proposal?: DocumentSplitProposal | null
    /** Open the dialog on mount (the « découpage à valider » queue links here). */
    autoOpen?: boolean
    /** Bump to open the dialog from elsewhere on the page (the proposal notice). */
    openRequest?: number
}

/**
 * canSplitDocument mirrors the backend rule (documents.Service.SplitDocument):
 * any PDF can be split except a split part, a child holding pages of its
 * bundle. An archive entry is a child of its archive but a whole file, so it
 * stays splittable (#1696: its proposal notice had no dialog to open).
 */
export function canSplitDocument(doc: {
    file_mime_type?: string | null
    parent_document_uid?: string | null
    parent_document_pages?: number[] | null
}): boolean {
    if (doc.file_mime_type !== 'application/pdf') return false
    return !(doc.parent_document_uid && (doc.parent_document_pages?.length ?? 0) > 0)
}

/** formatPages renders sorted pages as the dialog's free-text spec ("1-3, 5"). */
export function formatPages(pages: number[]): string {
    const sorted = [...pages].sort((a, b) => a - b)
    const parts: string[] = []
    let i = 0
    while (i < sorted.length) {
        let j = i
        while (j + 1 < sorted.length && sorted[j + 1] === sorted[j] + 1) j++
        parts.push(i === j ? String(sorted[i]) : `${sorted[i]}-${sorted[j]}`)
        i = j + 1
    }
    return parts.join(', ')
}

/** proposalRows turns a proposal into the dialog's prefilled rows. */
export function proposalRows(proposal: DocumentSplitProposal): SegmentRow[] {
    return proposal.segments.map((seg) => ({
        pages: formatPages(seg.pages),
        documentTypeCode: seg.document_type_code || '',
        name: '',
        confidence: seg.confidence,
        boilerplate: seg.boilerplate,
    }))
}

/**
 * parsePages turns a free-text page spec ("3-5", "1,2,10", "1, 3-4") into a
 * sorted, de-duplicated 1-based int[]. Returns null when the spec is malformed.
 */
export function parsePages(input: string): number[] | null {
    const trimmed = input.trim()
    if (trimmed === '') return null
    const set = new Set<number>()
    for (const rawPart of trimmed.split(',')) {
        const part = rawPart.trim()
        if (part === '') continue
        const rangeMatch = part.match(/^(\d+)\s*-\s*(\d+)$/)
        if (rangeMatch) {
            const start = parseInt(rangeMatch[1], 10)
            const end = parseInt(rangeMatch[2], 10)
            if (start < 1 || end < 1 || end < start) return null
            for (let p = start; p <= end; p++) set.add(p)
            continue
        }
        if (/^\d+$/.test(part)) {
            const p = parseInt(part, 10)
            if (p < 1) return null
            set.add(p)
            continue
        }
        return null
    }
    if (set.size === 0) return null
    return Array.from(set).sort((a, b) => a - b)
}

export function DocumentSplitDialog({ org, documentUid, pageCount: pageCountProp, proposal, autoOpen, openRequest }: DocumentSplitDialogProps) {
    const { t } = useTranslation('documents')
    const { request } = useApi()
    const queryClient = useQueryClient()
    const { data: documentTypes } = useDocumentTypes(org)

    const [open, setOpen] = useState(false)
    const [segments, setSegments] = useState<SegmentRow[]>([
        { pages: '', documentTypeCode: '', name: '' },
    ])
    const [submitting, setSubmitting] = useState(false)
    const [rejecting, setRejecting] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [derivedPageCount, setDerivedPageCount] = useState<number | undefined>(undefined)
    const pageCount = pageCountProp ?? derivedPageCount

    // When the caller doesn't already know the page count, derive it ourselves
    // the first time the dialog opens (headless pdfjs load — no <Page> render)
    // so the client-side out-of-range check has a bound to validate against
    // before the POST round-trip. Best-effort: a failure here just falls back
    // to server-side-only validation, it never blocks opening the dialog.
    useEffect(() => {
        if (!open || pageCountProp !== undefined || derivedPageCount !== undefined) return
        const contentUrl = `${apiBaseUrl}/api/v1/organizations/${org}/documents/${documentUid}/content`
        let cancelled = false
        pdfjs.getDocument({ url: contentUrl, withCredentials: true }).promise
            .then((pdf) => {
                if (!cancelled) setDerivedPageCount(pdf.numPages)
            })
            .catch(() => {
                // Ignore — server-side validation still catches out-of-range pages.
            })
        return () => {
            cancelled = true
        }
    }, [open, org, documentUid, pageCountProp, derivedPageCount])

    const sortedTypes = useMemo(() => {
        return (documentTypes || [])
            .filter((dt) => dt.code && dt.code !== 'MIXED')
            .slice()
            .sort((a, b) => (a.code || '').localeCompare(b.code || ''))
    }, [documentTypes])

    const reviewing = proposal?.status === 'pending'

    const reset = useCallback(() => {
        setSegments(reviewing && proposal ? proposalRows(proposal) : [{ pages: '', documentTypeCode: '', name: '' }])
        setError(null)
        setSubmitting(false)
        setRejecting(false)
    }, [reviewing, proposal])

    // The queue links here with ?review_split=true: open prefilled once the
    // proposal has loaded.
    const [autoOpened, setAutoOpened] = useState(false)
    useEffect(() => {
        if (autoOpen && reviewing && !autoOpened) {
            setAutoOpened(true)
            reset()
            setOpen(true)
        }
    }, [autoOpen, reviewing, autoOpened, reset])

    useEffect(() => {
        if (!openRequest) return
        reset()
        setOpen(true)
        // eslint-disable-next-line react-hooks/exhaustive-deps -- only a new request opens it
    }, [openRequest])

    const updateSegment = (index: number, patch: Partial<SegmentRow>) => {
        setSegments((prev) => prev.map((s, i) => (i === index ? { ...s, ...patch } : s)))
    }

    const addSegment = () => setSegments((prev) => [...prev, { pages: '', documentTypeCode: '', name: '' }])

    const removeSegment = (index: number) =>
        setSegments((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== index) : prev))

    const validate = (): { pages: number[]; documentTypeCode?: string; name?: string }[] | string => {
        if (segments.length === 0) return t('bundle.error_no_segments')
        const seen = new Map<number, boolean>()
        const out: { pages: number[]; documentTypeCode?: string; name?: string }[] = []
        for (const seg of segments) {
            const parsed = parsePages(seg.pages)
            if (parsed === null) return t('bundle.error_bad_pages', { value: seg.pages })
            if (parsed.length === 0) return t('bundle.error_empty_pages')
            for (const p of parsed) {
                if (pageCount && p > pageCount) return t('bundle.error_out_of_range', { page: p, count: pageCount })
                if (seen.has(p)) return t('bundle.error_overlap', { page: p })
                seen.set(p, true)
            }
            out.push({
                pages: parsed,
                documentTypeCode: seg.documentTypeCode || undefined,
                name: seg.name.trim() || undefined,
            })
        }
        return out
    }

    const invalidate = () => {
        queryClient.invalidateQueries({
            predicate: (query) => {
                const queryKey = query.queryKey as unknown[]
                if (queryKey.length >= 2 && queryKey[0] === 'get') {
                    const path = queryKey[1]
                    if (
                        path === '/api/v1/organizations/{orgCode}/documents/{documentUid}' ||
                        path === '/api/v1/organizations/{orgCode}/documents/{documentUid}/split-proposal' ||
                        path === '/api/v1/organizations/{orgCode}/documents'
                    ) {
                        return true
                    }
                }
                return false
            },
        })
    }

    const handleSubmit = async () => {
        const validated = validate()
        if (typeof validated === 'string') {
            setError(validated)
            return
        }
        setSubmitting(true)
        setError(null)
        try {
            await request(
                '/api/v1/organizations/{orgCode}/documents/{documentUid}/split',
                'POST',
                {
                    params: { path: { orgCode: org, documentUid } },
                    body: {
                        segments: validated.map((s) => ({
                            pages: s.pages,
                            document_type_code: s.documentTypeCode,
                            name: s.name,
                        })),
                    },
                },
            )
            invalidate()
            setOpen(false)
            reset()
        } catch {
            setError(t('bundle.error_generic'))
            setSubmitting(false)
        }
    }

    const handleReject = async () => {
        setRejecting(true)
        setError(null)
        try {
            await request(
                '/api/v1/organizations/{orgCode}/documents/{documentUid}/split-proposal/reject',
                'POST',
                { params: { path: { orgCode: org, documentUid } } },
            )
            invalidate()
            setOpen(false)
            reset()
        } catch {
            setError(t('bundle.error_reject'))
            setRejecting(false)
        }
    }

    const triggerLabel = reviewing ? t('bundle.review_action') : t('bundle.split_action')

    return (
        <>
            <Button
                variant={reviewing ? 'default' : 'outline'}
                className="p-2"
                onClick={() => {
                    reset()
                    setOpen(true)
                }}
                aria-label={triggerLabel}
                title={triggerLabel}
                data-testid={reviewing ? 'split-review-btn' : 'split-bundle-btn'}
            >
                <AppIcon path={mdiCallSplit} size="md" className="md:mr-1" />
                <span className="hidden md:inline">{triggerLabel}</span>
            </Button>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-2xl" data-testid="split-bundle-dialog">
                    <DialogHeader>
                        <DialogTitle>{reviewing ? t('bundle.review_title') : t('bundle.split_title')}</DialogTitle>
                        <DialogDescription>{reviewing ? t('bundle.review_description') : t('bundle.split_description')}</DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4 max-h-[50vh] overflow-y-auto pr-1">
                        {segments.map((seg, index) => (
                            <div
                                key={index}
                                className="border border-border rounded-lg p-3 space-y-2"
                                data-testid={`split-segment-${index}`}
                            >
                                <div className="flex items-center justify-between">
                                    <span className="flex flex-wrap items-center gap-2 text-sm font-medium text-foreground">
                                        {t('bundle.segment', { index: index + 1 })}
                                        {seg.confidence !== undefined && (
                                            <Badge variant="secondary" data-testid={`split-segment-confidence-${index}`}>
                                                {t('bundle.confidence', { value: Math.round(seg.confidence * 100) })}
                                            </Badge>
                                        )}
                                        {seg.boilerplate && (
                                            <Badge variant="outline" data-testid={`split-segment-boilerplate-${index}`}>
                                                {t('bundle.boilerplate')}
                                            </Badge>
                                        )}
                                    </span>
                                    {segments.length > 1 && (
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-6 w-6 text-muted-foreground"
                                            onClick={() => removeSegment(index)}
                                            aria-label={t('bundle.remove_segment')}
                                            title={t('bundle.remove_segment')}
                                            data-testid={`split-segment-remove-${index}`}
                                        >
                                            <AppIcon path={mdiClose} size="sm" />
                                        </Button>
                                    )}
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                                    <div>
                                        <Label className="text-xs text-muted-foreground">{t('bundle.pages_label')}</Label>
                                        <Input
                                            value={seg.pages}
                                            onChange={(e) => updateSegment(index, { pages: e.target.value })}
                                            placeholder={t('bundle.pages_placeholder')}
                                            data-testid={`split-segment-pages-${index}`}
                                        />
                                    </div>
                                    <div>
                                        <Label className="text-xs text-muted-foreground">{t('bundle.type_label')}</Label>
                                        <select
                                            value={seg.documentTypeCode}
                                            onChange={(e) => updateSegment(index, { documentTypeCode: e.target.value })}
                                            className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
                                            data-testid={`split-segment-type-${index}`}
                                        >
                                            <option value="">{t('bundle.type_auto')}</option>
                                            {sortedTypes.map((dt) => (
                                                <option key={dt.uid} value={dt.code}>
                                                    {dt.code}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <Label className="text-xs text-muted-foreground">{t('bundle.name_label')}</Label>
                                        <Input
                                            value={seg.name}
                                            onChange={(e) => updateSegment(index, { name: e.target.value })}
                                            placeholder={t('bundle.name_placeholder')}
                                            data-testid={`split-segment-name-${index}`}
                                        />
                                    </div>
                                </div>
                                <p className="text-xs text-muted-foreground">{t('bundle.pages_help')}</p>
                            </div>
                        ))}

                        <Button
                            variant="outline"
                            size="sm"
                            onClick={addSegment}
                            data-testid="split-add-segment-btn"
                        >
                            <AppIcon path={mdiPlus} size="sm" className="mr-1" />
                            {t('bundle.add_segment')}
                        </Button>
                    </div>

                    {error && (
                        <p className="text-sm text-destructive" data-testid="split-error">
                            {error}
                        </p>
                    )}

                    <DialogFooter>
                        {reviewing && (
                            <Button
                                variant="outline"
                                className="sm:mr-auto"
                                onClick={handleReject}
                                disabled={submitting || rejecting}
                                data-testid="split-reject-btn"
                            >
                                {rejecting ? t('bundle.rejecting') : t('bundle.reject_action')}
                            </Button>
                        )}
                        <Button
                            variant="outline"
                            onClick={() => setOpen(false)}
                            disabled={submitting || rejecting}
                            data-testid="split-cancel-btn"
                        >
                            {t('bundle.cancel')}
                        </Button>
                        <Button
                            onClick={handleSubmit}
                            disabled={submitting || rejecting}
                            data-testid="split-submit-btn"
                        >
                            {submitting ? t('bundle.splitting') : t('bundle.submit')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    )
}
